import { useState } from "react";
import { Navigation, MapPin, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

// Convert UTC ISO string or local time field to display HH:MM in local timezone
export const toLocalHHMM = (isoStr, localField) => {
  if (localField) return localField;
  if (!isoStr) return "--:--";
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch {
    return isoStr.split("T")[1]?.substring(0, 5) || "--:--";
  }
};

// GPS Helper - get current position as Promise
export function getGPSPosition(timeout = 10000) {
  return new Promise((resolve, reject) => {
    console.log("[GPS] getCurrentPosition called");
    if (!navigator.geolocation) { reject(new Error("Geolocation not supported")); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        console.log("[GPS] SUCCESS:", pos.coords.latitude, pos.coords.longitude);
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      },
      (err) => {
        console.error("[GPS] FAILED code:", err.code, err.message);
        reject(err);
      },
      { enableHighAccuracy: true, timeout, maximumAge: 0 }
    );
  });
}

// GPS Info Dialog
export function GPSInfoDialog({ open, onClose, t }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Navigation className="w-5 h-5 text-blue-600" />
            {t("gps.info_title")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm text-gray-700">
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">{t("gps.info_what_title")}</h4>
            <p>{t("gps.info_what_text")}</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">{t("gps.info_why_title")}</h4>
            <p>{t("gps.info_why_text")}</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">{t("gps.info_no_tracking_title")}</h4>
            <p>{t("gps.info_no_tracking_text")}</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">{t("gps.info_storage_title")}</h4>
            <p>{t("gps.info_storage_text")}</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">{t("gps.info_revoke_title")}</h4>
            <p>{t("gps.info_revoke_text")}</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700" data-testid="gps-info-close-btn">{t("gps.info_close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Segmented Control Component
export function SegmentedControl({ options, value, onChange, testId, disabled, compact = false }) {
  return (
    <div className={`segmented-control ${compact ? "compact" : ""}`} data-testid={testId}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`segmented-item ${value === option.value ? "active" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={() => !disabled && onChange(option.value)}
          disabled={disabled}
          data-testid={`${testId}-${option.value}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// GPS Error Dialog
export function GPSErrorDialog({ open, onRetry, onCancel }) {
  const [showHelp, setShowHelp] = useState(false);
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Navigation className="w-5 h-5" />
            Standortfreigabe erforderlich
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm text-gray-600">
            Damit du dich einstempeln kannst, musst du den Standortzugriff erlauben.
          </DialogDescription>
        </DialogHeader>
        {showHelp && (
          <div className="space-y-3 pt-1">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs font-bold text-gray-900 mb-1.5 flex items-center gap-1.5">
                <span className="w-5 h-5 bg-gray-900 text-white rounded flex items-center justify-center text-[10px] font-bold">i</span>
                iPhone / iPad (Safari)
              </p>
              <p className="text-xs text-gray-600 leading-relaxed">
                Einstellungen &rarr; Safari &rarr; Standort &rarr; <strong>&quot;Beim Verwenden der App erlauben&quot;</strong>
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs font-bold text-gray-900 mb-1.5 flex items-center gap-1.5">
                <span className="w-5 h-5 bg-green-600 text-white rounded flex items-center justify-center text-[10px] font-bold">A</span>
                Android (Chrome)
              </p>
              <p className="text-xs text-gray-600 leading-relaxed">
                Browser &rarr; Einstellungen &rarr; Website-Einstellungen &rarr; Standort &rarr; <strong>&quot;Erlauben&quot;</strong>
              </p>
            </div>
          </div>
        )}
        <DialogFooter className="flex-col gap-2 sm:flex-col pt-1">
          <Button onClick={onRetry} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="gps-retry-btn">
            Erneut versuchen
          </Button>
          {!showHelp && (
            <Button variant="outline" onClick={() => setShowHelp(true)} className="w-full" data-testid="gps-help-btn">
              Anleitung anzeigen
            </Button>
          )}
          <Button variant="ghost" onClick={onCancel} className="w-full text-gray-500" data-testid="gps-cancel-btn">
            Abbrechen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// GPS Location Badge
export function LocationBadge({ location, status, compact }) {
  if (!location) return null;
  const addr = location.address || `${location.lat?.toFixed(4)}, ${location.lng?.toFixed(4)}`;
  const shortAddr = addr.length > 40 ? addr.substring(0, 40) + "..." : addr;
  return (
    <div className="flex items-center gap-1.5" data-testid="location-badge">
      <MapPin className={`w-3 h-3 shrink-0 ${status === "vor_ort" ? "text-green-600" : status === "ausserhalb" ? "text-orange-500" : "text-gray-400"}`} />
      {status && (
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
          status === "vor_ort" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
        }`}>
          {status === "vor_ort" ? "Vor Ort" : "Ausserhalb"}
        </span>
      )}
      {!compact && <span className="text-[10px] text-gray-500 truncate">{shortAddr}</span>}
    </div>
  );
}
